import React, { useContext, useEffect, useState, useCallback } from 'react'
import Layout from './Layout'
import { MessageContext } from './Contexts'
import { FormControlLabel, Checkbox, Typography, Tooltip, Box } from '@mui/material'
import GridCard from './GridCard'
import GroupManager from './GroupManager'
import { QAListCategories, QAGetCategory, QAAddEditCategory, QAListBranches, QAAddEditBranch } from 'modules/Rest'
import CardHeader from './CardHeader'
import QAUserRightsCard from './QAUserRightsCard'
import QAPipelineCard from './QAPipelineCard'
import GridContainer from './GridContainer'
import { Link, useNavigate, useParams } from 'react-router'
import { setModes } from 'modules/Utils'
import { LoadingDotsIcon } from './Icons'
import { useSelectedDataRoom } from 'hooks/useSelectedDataRoom'

export default function QA() {
  const navigate = useNavigate()
  const { dataRoom } = useSelectedDataRoom()
  const { messageDispatch } = useContext(MessageContext)
  const [categoryData, setCategoryData] = useState(null)
  const [categoryClosed, setCategoryClosed] = useState(false)
  const [prevCategoryClosed, setPrevCategoryClosed] = useState(false)
  const [digestInterval, setDigestInterval] = useState(false)
  const [mode, setMode] = useState('')
  const [branches, setBranches] = useState([])
  const { qBranchId, categoryKey, dataRoomKey = '' } = useParams()

  const getBranches = useCallback(async () => {
    if (!dataRoomKey) {
      return []
    } else {
      try {
        const { data: { success, info, q_branches } } = await QAListBranches({data_room: dataRoomKey})
        if (success) {
          return q_branches
        } else {
          throw new Error(info ? info : 'An error occurred')
        }
      } catch (error) {
        messageDispatch({ type: 'warning', payload: error.message })
        return []
      }
    }
  }, [messageDispatch, dataRoomKey])

  const getCategories = useCallback(async () => {
    try {
      const { data: { success, info, categories } } = await QAListCategories({data_room: dataRoomKey, q_branch: qBranchId, assignable: true})
      if (success) {
        return categories
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
      return []
    }
  }, [messageDispatch, dataRoomKey, qBranchId])

  const getCategory = useCallback(async () => {
    try {
      setCategoryData(null)
      const { data: { success, info, category } } = await QAGetCategory({category: categoryKey})
      if (success) {
        setCategoryClosed(category.closed)
        setPrevCategoryClosed(category.closed)
        setDigestInterval(category.qa_digest_interval)
        setCategoryData(category)
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [categoryKey, messageDispatch])

  const saveCategorySettings = async (e, setMode) => {
    try {
      setMode('pending')
      const { data: { success, info } } = await QAAddEditCategory({category: categoryKey, closed: categoryClosed})
      if (success) {
        setPrevCategoryClosed(categoryClosed)
        setModes(setMode, ['completing', 1500, ''])
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setMode('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const switchBranch = useCallback((newBranchId) => {
    navigate(`/qa/${dataRoomKey}/${newBranchId}`)
  }, [navigate, dataRoomKey])

  const switchCategory = useCallback((newCategoryKey) => {
    navigate(`/qa/${dataRoomKey}/${qBranchId}/${newCategoryKey}`)
  }, [navigate, dataRoomKey, qBranchId])

  useEffect(() => {
    if (categoryKey) {
      getCategory()
    }
  }, [categoryKey, getCategory])

  return (
    <Layout>
      {!dataRoomKey  && <Box><Typography variant='h5'>Please select a data room</Typography></Box>}
      {dataRoom && !dataRoom.qa && !dataRoom.light &&<Typography variant='h5'>Please <Link to={`/data-room/${dataRoom.data_room}`}>activate Q&A</Link> for the data room</Typography>}
      {dataRoom && !dataRoom.qa && dataRoom.light && <Typography variant='h5'>Q&A is not available for Light Data Rooms</Typography>}
      {dataRoom && dataRoom.qa && <GridContainer>
        <GridCard uniformPadding={true} sm={12} style={{display: dataRoom.allow_multiple_qa ? "block" : "none"}}>
          <GroupManager
            getGroups={getBranches}
            editGroup={QAAddEditBranch}
            selectedGroupKey={qBranchId}
            switchGroup={switchBranch}
            setGroups={setBranches}
            groupName='Branch'
            groupAttr='q_branch'
            groupDisplayAttr='name'
            hideSelector={branches.length <= 1}
            hideDeleteBtn={branches.length <= 1}
            hideEditBtn={branches.length <= 1}
            labelHeading={branches.length === 1 && <i>Multiple Q&A</i>}
            labelAddBtn={branches.length <= 1 && 'Activate multiple Q&A'}
            labelAddInfo={branches.length <= 1 && 'Enter the name of the new Q&A branch in order to activate multiple Q&A.'}
          />
        </GridCard>

        {qBranchId && <GridCard uniformPadding={true} sm={12}>
          <GroupManager
            getGroups={getCategories}
            editGroup={QAAddEditCategory}
            selectedGroupKey={categoryKey}
            switchGroup={switchCategory}
            addEditParams={{q_branch: qBranchId}}
            groupName='Category'
            groupAttr='category'
            groupDisplayAttr='name'
          />
        </GridCard>}

        {categoryKey && <>
          <GridCard xs={12} md={8}>
            {categoryData && <QAUserRightsCard category={categoryData} refreshCategory={getCategory} />}
            {!categoryData && <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>}
          </GridCard>

          <GridCard xs={12} md={4}>
            {categoryData && <>
              <CardHeader
                mode={mode}
                setMode={setMode}
                action={saveCategorySettings}
                cancel={(e, setMode) => {
                  setCategoryClosed(prevCategoryClosed)
                  setMode('')
                }}
                title='Settings'
                headingElement='h6'
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={categoryClosed}
                    onChange={({ target: { checked } }) => setCategoryClosed(checked)}
                    disabled={mode !== 'editing'}
                  />
                }
                label='Closed for all operations'
              />
              <Tooltip title='The setting has been moved to the "Data rooms" page'>
                <Typography variant='body2'>
                  Q&A digest interval: {digestInterval} hours
                </Typography>
              </Tooltip>
            </>}
            {!categoryData && <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>}
          </GridCard>

          <GridCard xs={12}>
            {categoryData && <QAPipelineCard category={categoryData} refreshCategory={getCategory}/>}
            {!categoryData && <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>}
          </GridCard>
        </>}
      </GridContainer>}
    </Layout>
  )
}
